import { useEditorState, type Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import {
  LuAlignCenter,
  LuAlignJustify,
  LuAlignLeft,
  LuAlignRight,
  LuBaseline,
  LuBold,
  LuCode,
  LuHighlighter,
  LuImage,
  LuIndentDecrease,
  LuIndentIncrease,
  LuItalic,
  LuLink,
  LuList,
  LuListOrdered,
  LuListTodo,
  LuMaximize2,
  LuMinimize2,
  LuMinus,
  LuOmega,
  LuPaintbrush,
  LuRedo2,
  LuRemoveFormatting,
  LuSquareCode,
  LuStrikethrough,
  LuSubscript,
  LuSuperscript,
  LuTable,
  LuTextQuote,
  LuUnderline,
  LuUndo2,
} from '@platform/icon';
import {
  BLOCK_STYLES,
  FONT_FAMILIES,
  FONT_SIZES,
  HIGHLIGHT_COLOR_ROWS,
  LINE_HEIGHTS,
  SPECIAL_CHARS,
  TEXT_COLOR_ROWS,
  type BlockStyle,
  type BoardHeadingLevel,
} from './editorOptions.ts';
import {
  applyMarks,
  captureMarks,
  changeIndent,
  clearFormatting,
  normalizeLinkUrl,
  setBlockLineHeight,
} from './editorFormatting.ts';
import {
  ColorGrid,
  LinkForm,
  SymbolPicker,
  TableGridPicker,
  ToolButton,
  ToolDivider,
  ToolPopover,
  ToolSelect,
} from './EditorToolbarParts.tsx';
import { ImageContextBar, TableContextBar } from './EditorContextBars.tsx';

interface EditorToolbarProps {
  editor: Editor;
  /** 이미지 삽입 버튼 클릭 시 (파일 선택 트리거) */
  onPickImage: () => void;
  /** 이미지 업로드 진행 중 여부 (버튼 비활성화) */
  imageUploading?: boolean;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}

type Align = 'left' | 'center' | 'right' | 'justify';
type CopiedMarks = ReturnType<typeof captureMarks>;

const ICON = 'size-4';

/**
 * 에디터 상단 리본 툴바(Outlook 배치: 실행취소 · 글꼴 · 서식 · 단락 · 삽입).
 * 표·이미지를 선택하면 아래에 전용 도구 줄이 붙는다.
 * 선택이 바뀔 때마다 상태를 다시 읽도록 useEditorState 로 구독한다.
 */
export function EditorToolbar({
  editor,
  onPickImage,
  imageUploading,
  fullscreen,
  onToggleFullscreen,
}: EditorToolbarProps) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const textStyle = e.getAttributes('textStyle');
      const block = e.isActive('heading')
        ? (String(e.getAttributes('heading').level) as BlockStyle)
        : 'p';
      return {
        canUndo: e.can().undo(),
        canRedo: e.can().redo(),
        block,
        fontFamily: (textStyle.fontFamily as string | undefined) ?? '',
        fontSize: (textStyle.fontSize as string | undefined) ?? '',
        color: (textStyle.color as string | undefined) ?? '',
        highlight: (e.getAttributes('highlight').color as string | undefined) ?? '',
        lineHeight: (e.getAttributes(block === 'p' ? 'paragraph' : 'heading').lineHeight as
          | string
          | null) ?? '',
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        underline: e.isActive('underline'),
        strike: e.isActive('strike'),
        superscript: e.isActive('superscript'),
        subscript: e.isActive('subscript'),
        code: e.isActive('code'),
        align: (['center', 'right', 'justify'] as const).find((a) => e.isActive({ textAlign: a })) ??
          ('left' as Align),
        bulletList: e.isActive('bulletList'),
        orderedList: e.isActive('orderedList'),
        taskList: e.isActive('taskList'),
        blockquote: e.isActive('blockquote'),
        codeBlock: e.isActive('codeBlock'),
        link: e.isActive('link'),
        linkHref: (e.getAttributes('link').href as string | undefined) ?? '',
        selectionEmpty: e.state.selection.empty,
        inTable: e.isActive('table'),
        imageSelected: e.isActive('image'),
      };
    },
  });

  const [linkOpen, setLinkOpen] = useState(false);
  const [painter, setPainter] = useState<CopiedMarks | null>(null);

  // Ctrl+K 링크 (Outlook 단축키)
  useEffect(() => {
    const dom = editor.view.dom;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setLinkOpen(true);
      }
    };
    dom.addEventListener('keydown', onKey);
    return () => dom.removeEventListener('keydown', onKey);
  }, [editor]);

  // 서식 복사: 다음에 마우스로 끌어 선택한 영역에 한 번 적용하고 해제. Esc 로 취소.
  // mouseup 과 선택 갱신 순서가 브라우저마다 달라, 버튼을 뗀 뒤 선택이 생기는 시점에 적용한다.
  useEffect(() => {
    if (!painter) return;
    const dom = editor.view.dom;
    let dragged = false;
    let pressed = false;
    let done = false;
    const tryApply = () => {
      if (done || !dragged || pressed || editor.state.selection.empty) return;
      done = true;
      applyMarks(editor, painter);
      setPainter(null);
    };
    const onDown = () => {
      dragged = true;
      pressed = true;
    };
    const onUp = () => {
      pressed = false;
      setTimeout(tryApply, 0);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPainter(null);
    };
    dom.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keydown', onKey);
    editor.on('selectionUpdate', tryApply);
    return () => {
      dom.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keydown', onKey);
      editor.off('selectionUpdate', tryApply);
    };
  }, [editor, painter]);

  const chain = useCallback(() => editor.chain().focus(), [editor]);

  const setBlock = (value: string) => {
    if (value === 'p') chain().setParagraph().run();
    else chain().setHeading({ level: Number(value) as BoardHeadingLevel }).run();
  };

  const applyLink = (rawUrl: string, text: string) => {
    const href = normalizeLinkUrl(rawUrl);
    if (editor.state.selection.empty && !editor.isActive('link')) {
      // 삽입 직후 이어 치는 글자까지 링크가 되지 않게 stored mark 를 걷는다.
      chain()
        .insertContent({ type: 'text', text: text || href, marks: [{ type: 'link', attrs: { href } }] })
        .unsetMark('link')
        .run();
    } else {
      chain().extendMarkRange('link').setLink({ href }).run();
    }
    setLinkOpen(false);
  };

  return (
    // data-format-painter: 서식 복사 중 본문 커서를 바꾼다(index.css).
    <div
      data-format-painter={painter ? '' : undefined}
      className={[
        'board-toolbar z-20 border-b border-black/10 bg-neutral-50 dark:border-white/10 dark:bg-dark-surface',
        // 모바일은 툴바가 여러 줄이라 고정하면 본문이 가려진다 — sm 이상에서만 헤더(4rem) 아래에 붙인다.
        fullscreen ? '' : 'rounded-t-lg sm:sticky sm:top-16',
      ].join(' ')}
    >
      <div
        role="toolbar"
        aria-label="본문 서식"
        className="flex flex-wrap items-center gap-0.5 px-2 py-1.5"
      >
        {/* 실행 취소 */}
        <ToolButton label="실행 취소" shortcut="Ctrl+Z" disabled={!s.canUndo} onClick={() => chain().undo().run()}>
          <LuUndo2 className={ICON} />
        </ToolButton>
        <ToolButton label="다시 실행" shortcut="Ctrl+Y" disabled={!s.canRedo} onClick={() => chain().redo().run()}>
          <LuRedo2 className={ICON} />
        </ToolButton>
        <ToolDivider />

        {/* 글꼴 */}
        <ToolSelect label="스타일" value={s.block} options={BLOCK_STYLES} onChange={setBlock} className="w-[5.5rem]" />
        <ToolSelect
          label="글꼴"
          value={s.fontFamily}
          options={[{ value: '', label: '기본 글꼴' }, ...FONT_FAMILIES]}
          onChange={(v) => (v ? chain().setFontFamily(v).run() : chain().unsetFontFamily().run())}
          unknownLabel={(v) => v.split(',')[0].replace(/['"]/g, '')}
          className="w-[7.5rem]"
          previewFont
        />
        <ToolSelect
          label="글자 크기(pt)"
          value={s.fontSize}
          options={[{ value: '', label: '기본' }, ...FONT_SIZES]}
          onChange={(v) => (v ? chain().setFontSize(v).run() : chain().unsetFontSize().run())}
          className="w-[4.25rem]"
        />
        <ToolDivider />

        {/* 글자 서식 */}
        <ToolButton label="굵게" shortcut="Ctrl+B" active={s.bold} onClick={() => chain().toggleBold().run()}>
          <LuBold className={ICON} />
        </ToolButton>
        <ToolButton label="기울임꼴" shortcut="Ctrl+I" active={s.italic} onClick={() => chain().toggleItalic().run()}>
          <LuItalic className={ICON} />
        </ToolButton>
        <ToolButton label="밑줄" shortcut="Ctrl+U" active={s.underline} onClick={() => chain().toggleUnderline().run()}>
          <LuUnderline className={ICON} />
        </ToolButton>
        <ToolButton label="취소선" shortcut="Ctrl+Shift+S" active={s.strike} onClick={() => chain().toggleStrike().run()}>
          <LuStrikethrough className={ICON} />
        </ToolButton>
        <ToolButton label="위 첨자" shortcut="Ctrl+." active={s.superscript} onClick={() => chain().toggleSuperscript().run()}>
          <LuSuperscript className={ICON} />
        </ToolButton>
        <ToolButton label="아래 첨자" shortcut="Ctrl+," active={s.subscript} onClick={() => chain().toggleSubscript().run()}>
          <LuSubscript className={ICON} />
        </ToolButton>
        <ToolButton label="인라인 코드" shortcut="Ctrl+E" active={s.code} onClick={() => chain().toggleCode().run()}>
          <LuCode className={ICON} />
        </ToolButton>
        <ToolPopover
          label="글자 색"
          trigger={
            <span className="flex flex-col items-center">
              <LuBaseline className={ICON} />
              <span className="-mt-0.5 h-1 w-4 rounded-sm" style={{ backgroundColor: s.color || 'currentColor' }} />
            </span>
          }
        >
          {(close) => (
            <ColorGrid
              rows={TEXT_COLOR_ROWS}
              value={s.color}
              resetLabel="자동"
              onPick={(c) => {
                if (c) chain().setColor(c).run();
                else chain().unsetColor().run();
                close();
              }}
            />
          )}
        </ToolPopover>
        <ToolPopover
          label="형광펜"
          trigger={
            <span className="flex flex-col items-center">
              <LuHighlighter className={ICON} />
              <span className="-mt-0.5 h-1 w-4 rounded-sm" style={{ backgroundColor: s.highlight || '#fff59d' }} />
            </span>
          }
        >
          {(close) => (
            <ColorGrid
              rows={HIGHLIGHT_COLOR_ROWS}
              value={s.highlight}
              resetLabel="색 없음"
              onPick={(c) => {
                if (c) chain().setHighlight({ color: c }).run();
                else chain().unsetHighlight().run();
                close();
              }}
            />
          )}
        </ToolPopover>
        <ToolButton
          label={painter ? '서식 복사 취소' : '서식 복사 (붙일 곳을 끌어 선택)'}
          active={!!painter}
          onClick={() => setPainter(painter ? null : captureMarks(editor))}
        >
          <LuPaintbrush className={ICON} />
        </ToolButton>
        <ToolButton label="서식 지우기" onClick={() => clearFormatting(editor)}>
          <LuRemoveFormatting className={ICON} />
        </ToolButton>
        <ToolDivider />

        {/* 단락 */}
        <ToolButton label="왼쪽 맞춤" shortcut="Ctrl+Shift+L" active={s.align === 'left'} onClick={() => chain().setTextAlign('left').run()}>
          <LuAlignLeft className={ICON} />
        </ToolButton>
        <ToolButton label="가운데 맞춤" shortcut="Ctrl+Shift+E" active={s.align === 'center'} onClick={() => chain().setTextAlign('center').run()}>
          <LuAlignCenter className={ICON} />
        </ToolButton>
        <ToolButton label="오른쪽 맞춤" shortcut="Ctrl+Shift+R" active={s.align === 'right'} onClick={() => chain().setTextAlign('right').run()}>
          <LuAlignRight className={ICON} />
        </ToolButton>
        <ToolButton label="양쪽 맞춤" shortcut="Ctrl+Shift+J" active={s.align === 'justify'} onClick={() => chain().setTextAlign('justify').run()}>
          <LuAlignJustify className={ICON} />
        </ToolButton>
        <ToolSelect
          label="줄 간격"
          value={s.lineHeight}
          options={[{ value: '', label: '줄 간격' }, ...LINE_HEIGHTS]}
          onChange={(v) => setBlockLineHeight(editor, v || null)}
          className="w-[4.75rem]"
        />
        <ToolButton label="글머리 기호" shortcut="Ctrl+Shift+8" active={s.bulletList} onClick={() => chain().toggleBulletList().run()}>
          <LuList className={ICON} />
        </ToolButton>
        <ToolButton label="번호 매기기" shortcut="Ctrl+Shift+7" active={s.orderedList} onClick={() => chain().toggleOrderedList().run()}>
          <LuListOrdered className={ICON} />
        </ToolButton>
        <ToolButton label="체크리스트" shortcut="Ctrl+Shift+9" active={s.taskList} onClick={() => chain().toggleTaskList().run()}>
          <LuListTodo className={ICON} />
        </ToolButton>
        <ToolButton label="내어쓰기" shortcut="Ctrl+Shift+M" onClick={() => changeIndent(editor, -1)}>
          <LuIndentDecrease className={ICON} />
        </ToolButton>
        <ToolButton label="들여쓰기" shortcut="Ctrl+M" onClick={() => changeIndent(editor, 1)}>
          <LuIndentIncrease className={ICON} />
        </ToolButton>
        <ToolButton label="인용" shortcut="Ctrl+Shift+B" active={s.blockquote} onClick={() => chain().toggleBlockquote().run()}>
          <LuTextQuote className={ICON} />
        </ToolButton>
        <ToolButton label="코드 블록" shortcut="Ctrl+Alt+C" active={s.codeBlock} onClick={() => chain().toggleCodeBlock().run()}>
          <LuSquareCode className={ICON} />
        </ToolButton>
        <ToolDivider />

        {/* 삽입 */}
        <ToolPopover
          label="링크"
          active={s.link}
          open={linkOpen}
          onOpenChange={setLinkOpen}
          trigger={<LuLink className={ICON} />}
        >
          {() => (
            <LinkForm
              initialUrl={s.linkHref}
              withText={s.selectionEmpty && !s.link}
              onSubmit={applyLink}
              onRemove={
                s.link
                  ? () => {
                      chain().extendMarkRange('link').unsetLink().run();
                      setLinkOpen(false);
                    }
                  : undefined
              }
            />
          )}
        </ToolPopover>
        <ToolButton label="이미지 삽입" disabled={imageUploading} onClick={onPickImage}>
          {imageUploading ? <span className="text-xs">…</span> : <LuImage className={ICON} />}
        </ToolButton>
        <ToolPopover label="표 삽입" disabled={s.inTable} trigger={<LuTable className={ICON} />}>
          {(close) => (
            <TableGridPicker
              onPick={(rows, cols) => {
                chain().insertTable({ rows, cols, withHeaderRow: true }).run();
                close();
              }}
            />
          )}
        </ToolPopover>
        <ToolButton label="가로줄" onClick={() => chain().setHorizontalRule().run()}>
          <LuMinus className={ICON} />
        </ToolButton>
        <ToolPopover label="특수 문자" alignEnd trigger={<LuOmega className={ICON} />}>
          {() => <SymbolPicker symbols={SPECIAL_CHARS} onPick={(ch) => chain().insertContent(ch).run()} />}
        </ToolPopover>

        <span className="ml-auto" />
        <ToolButton label={fullscreen ? '전체 화면 닫기' : '전체 화면'} active={fullscreen} onClick={onToggleFullscreen}>
          {fullscreen ? <LuMinimize2 className={ICON} /> : <LuMaximize2 className={ICON} />}
        </ToolButton>
      </div>

      {s.inTable && <TableContextBar editor={editor} />}
      {s.imageSelected && <ImageContextBar editor={editor} />}
    </div>
  );
}
